import React, { useEffect, useState, useContext, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { App, Form, Select, Button, TimePicker, Input, Checkbox, Radio, Alert, Col, Row, Typography, Divider } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'

dayjs.extend(localizedFormat)

import isEmpty from 'lodash/isEmpty'
import flatMap from 'lodash/flatMap'
import { useManualQuery } from 'graphql-hooks'

import Api from '@vacationtracker/shared/services/api'
import { getLocationsApproverTeamsAndLabels, getLocationsTeamsAndLabelsShort, getNotificationById, getUsersForNotifictions } from '../../../graphql/custom-queries'

import * as logger from '../../../services/logger'
import { notificationStore } from '../../../context/notificationsContext/store'
import { useShouldEnableFeatures } from '../../../store/use-should-enable-features'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthUserSlice } from '../../../store/auth-user-slice'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import { WorkWeek } from '../../../util/WorkWeek'
import { filterOptions } from '../../../util/filter-options'
import { getNotificationTimezoneFromDraftOrDefault, resolveNotificationTimezone } from '../../../util/notification-timezone'
import { redirectIfEntityNotFound } from '../../../util/entity-not-found-redirect'
import timezone from '@vacationtracker/shared/data/timezone'

import CircularProgress from '../../../components/circular-progress'
import NotificationDestinations from '../../../components/notification-destination'
import NotificationSummaryInfo from '../../../components/notification-summary-info'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { FrontendUrls } from '../../../types/urls'
import { PageHeader } from '../../../design-system/PageHeader'
import FilterAdvanced from '@vacationtracker/shared/components/filter-advanced'
import { availableLanguages } from '@vacationtracker/shared/i18n'
import { dayOfWeekMap } from '../../../constants'

import { INotificationForm, INotificationsPage, IDestination, IDestinationForm, IGetNotificationById } from '../../../types/notifications'
import { IGetLocationsApproverTeamsAndLabelsShort, IGetLocationsTeamsAndLabelsShort, IGetUsersForNotifications } from '../../../types/custom-queries'
import { IGetLabelsShort } from '../../../types/labels'
import { IFilter } from '@vacationtracker/shared/types/filter'
import { ITeamShort } from '@vacationtracker/shared/types/team'
import { ILocationShort } from '@vacationtracker/shared/types/location'
import { ISelected } from '@vacationtracker/shared/components/filter-advanced/types'
import { NotificationFrequency, NotificationPlatform } from '@vacationtracker/shared/types/notification-v2'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { SubscriptionPlanEnum } from '@vacationtracker/shared/types/company'
import { FeatureFlagEnum } from '@vacationtracker/shared/types/feature-flags'
import { IGetTeamsShort } from '../../../types/teams'
import { IGetLocationsShort } from '../../../types/locations'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'
export interface ISelectedDestination {
  type: string
  values: string | string[]
}

const { Option } = Select
const { Title, Paragraph, Text } = Typography

const defaultNotificationState = {
  id: '',
  name: '',
  firstDayOfWeek: 1,
  frequency: 'DAILY',
  day: undefined,
  hour: 10,
  minute: Number('00'),
  timezone: '',
  enabled: true,
  locations: [],
  teams: [],
  labels: [],
  groupBy: 'TEAM',
  sendEmpty: false,
  locale: LocaleEnum.en,
  sendOnDays: [1, 2, 3, 4, 5],
  owner: {
    id: '',
    name: '',
  },
}

const NotificationsWeeklyDailyFormPage = ({ match }: INotificationsPage): React.ReactElement => {
  const [form] = Form.useForm()
  const { notification: antdNotification } = App.useApp()

  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const {
    authUser: { id, role, locale, hourFormat, location: authUserLocation },
  } = useAppSelector(selectAuthUserSlice)
  const userTimezonePreference = authUserLocation?.timezone
  const history = useHistory()
  const { formatMessage } = useIntl()
  const { actionNotifications, setActionNotifications } = useContext(notificationStore)

  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingNotification, setIsLoadingNotification] = useState(true)
  const [isApiInProgress, setIsApiInProgress] = useState(false)
  const [teams, setTeams] = useState<ITeamShort[]>([])
  const [locations, setLocations] = useState<ILocationShort[]>([])
  const [labels, setLabels] = useState<IGetLabelsShort[]>([])
  const [initialFilterValues, setInitialFilterValues] = useState([{}])
  const [initialDestination, setInitialDestination] = useState<ISelectedDestination[] | [{}]>([{}])
  const [filter, setFilter] = useState<IFilter>({
    locationIds: [],
    teamIds: [],
    labelIds: [],
  })
  const [notification, setNotification] = useState<INotificationForm>(defaultNotificationState as INotificationForm)
  const [notificationFrequency, setNotificationFrequency] = useState<NotificationFrequency | undefined>('DAILY')
  const [totalUsers, setTotalUsers] = useState<number>(0)
  const [channelName, setChannelName] = useState('')
  const [shouldResetFilters, setShouldResetFilters] = useState(false)
  const [shouldResetNotificationDestination, setShouldResetNotificationDestination] = useState(false)
  const isUpadateNotification = !!match.params.id

  const abortControllerRef = useRef<AbortController>()

  const shouldEnableLabels = useShouldEnableFeatures(SubscriptionPlanEnum.complete, FeatureFlagEnum.labels)
  const amIAdmin = role.toLowerCase() === 'admin'
  // TODO: if there are no user show meaningful message
  const amIApprover = role.toLowerCase() === 'approver'

  const [getUsersForNotifictionsQuery] = useManualQuery<
    IGetUsersForNotifications,
    {
      locationIds: string[]
      teamIds: string[]
      labelIds: string[]
    }
  >(getUsersForNotifictions)
  const [getLocationsTeamsAndLabelsShortQuery] = useManualQuery<IGetLocationsTeamsAndLabelsShort>(getLocationsTeamsAndLabelsShort)
  const [getLocationsApproverTeamsAndLabelsQuery] = useManualQuery<IGetLocationsApproverTeamsAndLabelsShort>(getLocationsApproverTeamsAndLabels)
  const [getNotificationByIdQuery] = useManualQuery<IGetNotificationById, { id: string }>(getNotificationById)

  useEffect(() => {
    abortControllerRef.current = new AbortController()
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  useEffect(() => {
    if (isUpadateNotification && isLoadingNotification) {
      fetchNotification(match.params.id)
    } else {
      const notificationForm = localStorage.getItem('notificationForm')
      if (notificationForm) {
        const notificationFormObject = JSON.parse(notificationForm)
        const initialTz = getNotificationTimezoneFromDraftOrDefault(notificationFormObject.timezone, userTimezonePreference)
        setNotificationFrequency(notificationFormObject.frequency)
        form.setFieldsValue({ ...notificationFormObject, timezone: initialTz })
        handleSetInitialDestinationFromStorage(notificationFormObject)
      } else {
        const initialTz = resolveNotificationTimezone(userTimezonePreference)
        const draft = {
          ...defaultNotificationState,
          locale,
          timezone: initialTz,
          timestamp: Date.now(),
        }
        localStorage.setItem('notificationForm', JSON.stringify(draft))
        form.setFieldsValue(draft)
        setInitialDestination([{}])
      }

      setIsLoadingNotification(false)
    }
  }, [match.params.id])

  useEffect(() => {
    if (isLoading) {
      fetchData()
    }
  }, [isLoading])

  const handleResetForm = () => {
    const initialTz = resolveNotificationTimezone(userTimezonePreference)
    const defaultNotification = {
      ...defaultNotificationState,
      locale,
      timezone: initialTz,
      timestamp: Date.now(),
    }
    localStorage.setItem('notificationForm', JSON.stringify(defaultNotification))
    form.resetFields()
    form.setFieldsValue(defaultNotification)
    setInitialDestination([{}])
    setFilter({
      locationIds: [],
      teamIds: [],
      labelIds: [],
    })
    setInitialFilterValues([{}])
    setShouldResetFilters(true)
    setShouldResetNotificationDestination(true)
  }

  const handleSetInitialDestinationFromStorage = (notificationFormObject) => {
    setInitialDestination(() => {
      const destinations: ISelectedDestination[] | [{}] = []
      for (const [key] of Object.entries(notificationFormObject)) {
        if (key.includes('destination-') && !isEmpty(notificationFormObject[key])) {
          destinations.push({
            type: notificationFormObject[key].type,
            values: notificationFormObject[key].addresses,
          })
        }
      }
      return destinations.length > 0 ? destinations : [{}]
    })
  }

  const getTotalUsers = async (locationIds: string[] = [], teamIds: string[] = [], labelIds: string[] = []) => {
    try {
      const abortController = abortControllerRef.current
      const usersResponse = await getUsersForNotifictionsQuery({
        variables: { locationIds, teamIds, labelIds },
        fetchOptionsOverrides: {
          signal: abortController?.signal,
        },
      })
      if (!usersResponse.data || usersResponse.error) throw usersResponse.error
      if (!abortController?.signal.aborted) {
        setTotalUsers(usersResponse.data.getUsersFiltered.totalUsers)
      }
    } catch (err) {
      if (err.fetchError?.name === 'AbortError') {
        // Not an error, we aborted the fetch action
        return
      }
      throw err
    }
  }

  const fetchData = async () => {
    try {
      let response
      const abortController = abortControllerRef.current
      if (amIAdmin) {
        response = await getLocationsTeamsAndLabelsShortQuery({
          fetchOptionsOverrides: {
            signal: abortController?.signal,
          },
        })
        if (!abortController?.signal.aborted) {
          setTeams(response.data.getTeamListV2 as IGetTeamsShort[])
        }
      }
      if (amIApprover) {
        response = await getLocationsApproverTeamsAndLabelsQuery({
          variables: { id },
          fetchOptionsOverrides: {
            signal: abortController?.signal,
          },
        })
        if (!abortController?.signal.aborted) {
          setTeams(response.data.getUser.approverToTeams as ITeamShort[])
        }
      }
      if (!isUpadateNotification) {
        getTotalUsers()
      }
      if (response?.data && !abortController?.signal.aborted) {
        setLocations(response.data.getLocationListV2 as IGetLocationsShort[])
        setLabels(response.data.getLabelListV2 as IGetLabelsShort[])
      }
      if (!abortController?.signal.aborted) {
        setIsLoading(false)
      }
    } catch (err) {
      if (err.fetchError?.name === 'AbortError') {
        // Not an error, we aborted the fetch action
        return
      }
      logger.error('ERROR FETCH DATA', err)
    }
  }

  const fetchNotification = async (id) => {
    try {
      const abortController = abortControllerRef.current
      const response = await getNotificationByIdQuery({
        variables: { id },
        fetchOptionsOverrides: {
          signal: abortController?.signal,
        },
      })
      if (!response.data || response.error) throw response.error
      if (!redirectIfEntityNotFound(response.data.getNotification, FrontendUrls.notifications, history)) return
      const notificationData = response.data.getNotification
      notificationData.destinations.map((destination, index) => {
        notificationData[`destination-${index}`] = {
          type: destination.platform === 'email' ? 'Email' : formatMessage({ id: 'components.selectChannelForm.channel' }),
          addresses: destination.platform === 'email' ? destination.addresses : destination.addresses[0],
          msGroup: destination.platform === 'microsoft' ? destination.msGroup : undefined,
        }
      })

      if (!abortController?.signal.aborted) {
        setInitialDestination(
          notificationData.destinations.map((destination) => {
            return {
              type: destination.platform === 'email' ? 'Email' : formatMessage({ id: 'components.selectChannelForm.channel' }),
              values: destination.platform === 'email' ? destination.addresses : destination.addresses[0],
            }
          })
        )

        await getTotalUsers(notificationData.locations, notificationData.teams, notificationData.labels)

        setInitialFilterValues(() => {
          const data: ISelected[] = []

          if (notificationData) {
            if (notificationData.locations.length > 0) {
              data.push({ type: 'Locations', values: notificationData.locations })
            }
            if (notificationData.teams.length > 0) {
              data.push({ type: 'Departments', values: notificationData.teams })
            }
            if (notificationData?.labels && notificationData.labels.length > 0) {
              data.push({ type: 'Labels', values: notificationData.labels })
            }
          }
          setFilter({
            labelIds: notificationData.labels,
            locationIds: notificationData.locations,
            teamIds: notificationData.teams,
          })

          if (data.length === 0) {
            return [{}]
          }
          return data
        })

        if (notificationData.frequency === 'WEEKLY') {
          notificationData.currentWeek = notificationData.currentWeek ? 1 : 0
        }

        setNotificationFrequency(notificationData.frequency)
        setNotification(notificationData)
        setIsLoadingNotification(false)
      }
    } catch (err) {
      if (err.fetchError?.name === 'AbortError') {
        // Not an error, we aborted the fetch action
        return
      }
      logger.error('ERROR FETCHING NOTIFICATION', err)
    }
  }

  const onFinish = async () => {
    setIsApiInProgress(true)
    let response
    try {
      const values = await form.validateFields()

      const destinations: IDestination[] = []
      for (const [key, val] of Object.entries(values)) {
        if (key.includes('destination-')) {
          const value: IDestinationForm = val as IDestinationForm
          const platform: NotificationPlatform = authCompany?.platform as NotificationPlatform
          destinations.push({
            platform: value.type === 'Email' ? 'email' : platform,
            addresses: value.type === 'Email' ? value.addresses : [value.addresses as string],
            msGroup: value.msGroup,
          })
        }
      }

      const data = {
        name: values.name,
        firstDayOfWeek: values.firstDayOfWeek,
        frequency: values.frequency,
        destinations,
        day: values.day,
        hour: notification.hour,
        minute: notification.minute,
        timezone: values.timezone,
        enabled: isUpadateNotification ? notification.enabled : true,
        locations: filter.locationIds,
        teams: filter.teamIds,
        labels: filter.labelIds,
        groupBy: values.groupBy,
        sendEmpty: values.sendEmpty,
        currentWeek: notificationFrequency === 'WEEKLY' ? Boolean(values.currentWeek) : undefined,
        sendOnDays: values.sendOnDays,
        locale: values.locale,
        version: 2,
      }

      if (notificationFrequency === 'WEEKLY') {
        data.day = values.day
        delete data.sendOnDays
      }

      response = await Api.post('/core/event', {
        eventType: isUpadateNotification ? 'NOTIFICATION_UPDATED' : 'NOTIFICATION_CREATED',
        eventGroup: 'NOTIFICATION',
        notificationId: isUpadateNotification ? match.params.id : undefined,
        ...data,
      })

      antdNotification.open({
        key: response.correlationId,
        message: match.params.id
          ? formatMessage({ id: 'notificationsForm.updateInProgress' }, { notificationName: response.name })
          : formatMessage({ id: 'notificationsForm.createInProgress' }, { notificationName: response.name }),
        icon: <LoadingOutlined />,
        duration: 0,
      })
      setActionNotifications([...actionNotifications, response.correlationId])

      if (!isUpadateNotification) {
        if (localStorage.getItem('notificationForm')) {
          localStorage.removeItem('notificationForm')
        }

        if (localStorage.getItem('notificationFormFilters')) {
          localStorage.removeItem('notificationFormFilters')
        }
      }

      setIsApiInProgress(false)
      history.push(FrontendUrls.notifications)

      if (!match.params.id) {
        form.resetFields()
      }
    } catch (error) {
      logger.error('ERROR HANDLE SUBMIT', error)
      setIsApiInProgress(false)

      const errorMessage = response?.correlationId
        ? formatMessage({ id: 'error.saveFailed' }, { correlationId: response.correlationId })
        : error.response?.data?.message
          ? error.response?.data?.message
          : error.message
            ? error.message
            : JSON.stringify(error)

      const msg = (errorMessage || '').toLowerCase()

      const checkInvalidEmail = msg.includes('valid email') && msg.includes('destinations[') && msg.includes('.addresses[')

      const prettyMessage = checkInvalidEmail ? formatMessage({ id: 'notificationsForm.invalidEmail' }) : errorMessage

      antdNotification.error({
        message: formatMessage({ id: 'error.saveFailed' }),
        description: prettyMessage,
        duration: 0,
      })
    }
  }

  const onChangeTime = (time) => {
    setShouldResetFilters(false)
    setShouldResetNotificationDestination(false)
    setNotification((prevValue) => ({
      ...prevValue,
      hour: Number(dayjs(time).format('HH')),
      minute: Number(dayjs(time).format('mm')),
    }))
  }

  const handleLocalStorageDestinationChanges = (newValues) => {
    if (!isUpadateNotification) {
      localStorage.setItem('notificationForm', JSON.stringify(newValues))
    }
  }

  const handleValuesChange = (changedValues) => {
    setShouldResetFilters(false)
    setShouldResetNotificationDestination(false)
    const fieldName = Object.keys(changedValues)[0]
    const currentNotification = form.getFieldsValue(true)

    if (!isUpadateNotification) {
      localStorage.setItem('notificationForm', JSON.stringify(currentNotification))
    }

    if (fieldName === 'frequency') {
      const value = changedValues[fieldName]
      setNotificationFrequency(value)
      if (value === 'DAILY') {
        form.setFieldsValue({ currentWeek: undefined, day: undefined, sendEmpty: notification.id ? notification.sendEmpty : false })
      }
      if (value === 'WEEKLY') {
        form.setFieldsValue({ currentWeek: notification.currentWeek || 0, day: notification.day, sendEmpty: notification.id ? notification.sendEmpty : true })
      }
    }
  }

  const handleFilter = (filterArg: IFilter) => {
    setFilter(filterArg)
    setShouldResetFilters(false)
    setShouldResetNotificationDestination(false)
    getTotalUsers(filterArg.locationIds, filterArg.teamIds, filterArg.labelIds)
  }

  return (
    <div className="main-content vt-notifications-form-page">
      <PageHeader
        title={isUpadateNotification ? <IntlMessages id="notifications.update" /> : <IntlMessages id="notifications.add" />}
        parent={[{ label: <IntlMessages id="sidebar.timeOffUpdates" />, to: FrontendUrls.notifications }]}
      />
      <Text>
        <IntlMessages id="notifications.dailyWeeklyNotifications.descriptionInfo" />
      </Text>
      <div className="main-content-body" style={{ marginTop: '40px' }}>
        <div className="form-layout">
          {(match.params.id && isLoadingNotification) || isLoading ? (
            <CircularProgress />
          ) : (
            <>
              {notification.error && <Alert message={<IntlMessages id={notification.error} />} type="error" showIcon style={{ marginBottom: '20px' }} />}
              <Form
                layout="vertical"
                form={form}
                name="notificationsForm"
                initialValues={notification}
                className="daily-weekly-notification-form"
                onFinish={onFinish}
                scrollToFirstError
                labelWrap
                onValuesChange={handleValuesChange}
                size="large"
              >
                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                    <Title level={4}>
                      <IntlMessages id="notifications.dailyWeeklyNotifications.generalTitle" />
                    </Title>
                    <Paragraph type="secondary">
                      <IntlMessages id="notifications.dailyWeeklyNotifications.generalDescription" />
                    </Paragraph>
                  </Col>
                  <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                    <Form.Item label={<IntlMessages id="notificationsForm.name" />} name="name" rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}>
                      <Input data-qa="notification-name" />
                    </Form.Item>
                    <FilterAdvanced
                      isForm={true}
                      data={{
                        Locations: locations,
                        Departments: teams,
                        Labels: labels,
                      }}
                      onChangeFilter={handleFilter}
                      showLabels={shouldEnableLabels}
                      toolTipInfo="notificationsForm.advancedFilter.tooltipInfo"
                      initialValues={initialFilterValues}
                      filterByWidth={160}
                      page={isUpadateNotification ? undefined : 'notificationForm'}
                      shouldReset={shouldResetFilters}
                    />
                  </Col>
                </Row>

                <Divider />

                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                    <Title level={4}>
                      <IntlMessages id="notifications.dailyWeeklyNotifications.scheduleAndDelivery" />
                    </Title>
                    <Paragraph type="secondary">
                      <IntlMessages id="notifications.dailyWeeklyNotifications.scheduleAndDeliveryDescription" />
                    </Paragraph>
                  </Col>
                  <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                    <Form.Item
                      name="frequency"
                      label={<IntlMessages id="notifications.frequency" />}
                      rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                    >
                      <Select style={{ width: 160 }}>
                        {['DAILY', 'WEEKLY'].map((f) => (
                          <Option key={f} value={f}>
                            <IntlMessages id={`app.${f.toLowerCase()}`} />
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>

                    {notificationFrequency === 'WEEKLY' && (
                      <>
                        <Form.Item
                          name="currentWeek"
                          label={<IntlMessages id="notificationsForm.periodCovered" />}
                          rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                        >
                          <Select style={{ width: 160 }}>
                            <Option key="NextWeek" value={0}>
                              <IntlMessages id="notificationsForm.nextWeek" />
                            </Option>
                            <Option key="CurrentWeek" value={1}>
                              <IntlMessages id="notificationsForm.currentWeek" />
                            </Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="firstDayOfWeek"
                          label={<IntlMessages id="notifications.weekStartsOn" />}
                          rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                        >
                          <Select style={{ width: 160 }}>
                            <Option key="0" value={0}>
                              <IntlMessages id="app.sunday" />
                            </Option>
                            <Option key="1" value={1}>
                              <IntlMessages id="app.monday" />
                            </Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="day"
                          label={<IntlMessages id="notificationsForm.sendOnDays" />}
                          rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                        >
                          <Radio.Group>
                            {dayOfWeekMap.map((day) => (
                              <Radio key={day.value} value={day.value}>
                                <IntlMessages id={`${day.name}`} />
                              </Radio>
                            ))}
                          </Radio.Group>
                        </Form.Item>
                      </>
                    )}

                    {notificationFrequency === 'DAILY' && (
                      <Form.Item
                        name="sendOnDays"
                        label={<IntlMessages id="notificationsForm.sendOnDays" />}
                        rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                      >
                        <Checkbox.Group>
                          {WorkWeek.map((day) => (
                            <Checkbox key={day.value} value={day.value}>
                              {day.label}
                            </Checkbox>
                          ))}
                        </Checkbox.Group>
                      </Form.Item>
                    )}

                    <div className="form-inline">
                      <Form.Item style={{ width: 125, paddingRight: 20 }} label={<IntlMessages id="app.time" />}>
                        <TimePicker
                          minuteStep={15}
                          format={hourFormat === HourFormatEnum.twelve ? 'h:mm A' : 'HH:mm'}
                          onCalendarChange={onChangeTime}
                          value={
                            notification?.hour != null && notification?.minute != null
                              ? dayjs(`${notification.hour.toString().padStart(2, '0')}:${notification.minute.toString().padStart(2, '0')}`, 'HH:mm')
                              : null
                          }
                          defaultValue={dayjs('10:00', 'HH:mm')}
                          use12Hours={hourFormat === HourFormatEnum.twelve}
                        />
                      </Form.Item>
                      <Form.Item
                        name="timezone"
                        style={{ width: 'calc(100% - 125px)' }}
                        label={<IntlMessages id="app.timezone" />}
                        rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}
                      >
                        <Select style={{ width: '100%' }} showSearch filterOption={filterOptions} data-qa="timezone-select">
                          {timezone.map((tz) => (
                            <Option key={tz.id} value={tz.id}>
                              {tz.text}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>

                    <NotificationDestinations
                      platform={authCompany?.platform as NotificationPlatform}
                      setChannelName={setChannelName}
                      form={form}
                      initialDestination={initialDestination}
                      notification={notification}
                      onValueChange={handleLocalStorageDestinationChanges}
                      shouldReset={shouldResetNotificationDestination}
                    />
                  </Col>
                </Row>

                <Divider />

                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                    <Title level={4}>
                      <IntlMessages id="notifications.dailyWeeklyNotifications.displayOptionsTitle" />
                    </Title>
                    <Paragraph type="secondary">
                      <IntlMessages id="notifications.dailyWeeklyNotifications.displayOptionsDescription" />
                    </Paragraph>
                  </Col>
                  <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                    <Form.Item name="groupBy" label={<IntlMessages id="notificationsForm.groupBy" />} tooltip={<IntlMessages id="notificationsForm.groupBy.tooltip" />}>
                      <Select style={{ width: 200 }}>
                        {['TEAM', 'LOCATION', 'NONE'].map((g) => {
                          if (g === 'TEAM') {
                            return (
                              <Option key={g} value={g}>
                                <IntlMessages id="app.department" />
                              </Option>
                            )
                          }
                          if (g === 'LOCATION') {
                            return (
                              <Option key={g} value={g}>
                                <IntlMessages id="app.location" />
                              </Option>
                            )
                          }
                          return (
                            <Option key={g} value={g}>
                              <IntlMessages id={`notificationsForm.${g}`} />
                            </Option>
                          )
                        })}
                      </Select>
                    </Form.Item>
                    <Form.Item name="sendEmpty" label={<IntlMessages id="notificationsForm.sendEmpty" />}>
                      <Radio.Group className="vt-notification-radio-btn">
                        <Radio value={true}>
                          <IntlMessages id="notificationsForm.sendEmptyYes" />
                        </Radio>
                        <Radio value={false}>
                          <IntlMessages id="notificationsForm.sendEmptyNo" />
                        </Radio>
                      </Radio.Group>
                    </Form.Item>
                    <Form.Item name="locale" label={<IntlMessages id="notificationsForm.locale" />} rules={[{ required: true, message: <IntlMessages id="form.inputRequired" /> }]}>
                      <Select style={{ width: 200 }} defaultValue={locale || LocaleEnum.en}>
                        {Object.values(availableLanguages).map((language) => (
                          <Option key={language.locale} value={language.locale}>
                            {language.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                <Divider />

                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24}>
                    <Title level={4}>
                      <IntlMessages id="notifications.dailyWeeklyNotifications.summaryTitle" />
                    </Title>
                    <Paragraph type="secondary">
                      <IntlMessages id="notifications.dailyWeeklyNotifications.summaryDescription" />
                    </Paragraph>
                  </Col>
                  <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                    <Form.Item shouldUpdate>
                      {({ getFieldValue, getFieldsValue }) => {
                        const formData = getFieldsValue(true)

                        const numberOfEmailAddress = flatMap(formData, (item) => {
                          if (item?.type === 'Email' && item.addresses) {
                            return item.addresses.length
                          }
                        }).filter(Boolean)[0]

                        return (
                          <div className="vt-notification-summary-box">
                            <div className="vt-notification-message">
                              <NotificationSummaryInfo
                                filters={filter}
                                noOfUsers={totalUsers}
                                channelName={channelName}
                                numberOfEmailAddress={numberOfEmailAddress}
                                sendOnDays={getFieldValue('sendOnDays')}
                                day={getFieldValue('day')}
                                hour={notification.hour}
                                minute={notification.minute}
                                frequency={notificationFrequency}
                              />
                            </div>
                          </div>
                        )
                      }}
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
                  <Col xxl={8} xl={8} lg={6} md={6} sm={24} xs={24} />
                  <Col xxl={16} xl={16} lg={18} md={18} sm={24} xs={24}>
                    <Form.Item style={{ textAlign: 'right' }}>
                      {!match.params.id && (
                        <Button type="text" onClick={handleResetForm}>
                          <IntlMessages id="app.reset" />
                        </Button>
                      )}
                      <Button className="button-right" type="default" onClick={() => history.push(FrontendUrls.notifications)}>
                        <IntlMessages id="app.cancel" />
                      </Button>
                      <Button
                        className="button-right"
                        htmlType="submit"
                        type="primary"
                        loading={isLoadingNotification || isLoading || isApiInProgress}
                        data-qa="submit-notification"
                      >
                        {isUpadateNotification ? <IntlMessages id="notificationsForm.update" /> : <IntlMessages id="notificationsForm.submit" />}
                      </Button>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default NotificationsWeeklyDailyFormPage
